import { combineReducers } from 'redux';
import { createReducer } from '@reduxjs/toolkit';

import * as actions from '../../actions/library/bundlePackage';
import {
  Bundles,
  FormItemEntity,
  ItemNameEntity,
  ItemVariant,
  Outlets,
  SalesTypesEntity,
} from '../../actions/library/bundlePackage/types';

type SalesTypesForm = SalesTypesEntity & FormItemEntity;
type OutletsForm = Outlets & FormItemEntity;

const outlets = createReducer([] as Outlets[], (builder) => {
  builder.addCase(actions.setOutlets, (state, { payload }) => {
    return payload;
  });
});
interface BundlesEntity {
  list?: Bundles[];
  isCompleted?: boolean;
  nextCursor?: string;
}
const getBundlesInitialState = (): BundlesEntity => ({
  list: [],
  isCompleted: false,
  nextCursor: null as unknown as undefined,
});
const bundles = createReducer(getBundlesInitialState(), (builder) => {
  builder
    .addCase(actions.setBundlesList, (state, { payload }) => {
      state.list = payload;
    })
    .addCase(actions.setIsCompleted, (state, { payload }) => {
      state.isCompleted = payload;
    })
    .addCase(actions.setNextCursor, (state, { payload }) => {
      state.nextCursor = payload;
    })
    .addCase(actions.resetBundlesListState, () => getBundlesInitialState());
});

type BundlePackageDetailEntity = Bundles & { isActive?: boolean };
const bundlePackageDetail = createReducer(
  null as unknown as BundlePackageDetailEntity,
  (builder) => {
    builder
      .addCase(actions.setBundleDetail, (state, { payload }) => {
        return payload;
      })
      .addCase(
        actions.onClickCreateBundle,
        (): BundlePackageDetailEntity => ({
          isActive: true,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          sales_types: [],
          // eslint-disable-next-line @typescript-eslint/naming-convention
          outlet_ids: [],
          // eslint-disable-next-line @typescript-eslint/naming-convention
          bundle_components: [],
        })
      );
  }
);

const isShowingDetail = createReducer(false, (builder) => {
  builder
    .addCase(actions.setInitialBundle, () => true)
    .addCase(actions.setBundleDetail, () => true)
    .addCase(actions.unsetBundleDetail, () => false)
    .addCase(actions.refreshList, (state, { payload }) => {
      const { isShowingDetail: _isShowingDetail } = payload;
      return _isShowingDetail;
    });
});

const selectedOutlets = createReducer([] as OutletsForm[], (builder) => {
  builder.addCase(actions.setSelectedOutlets, (state, { payload }) => {
    return payload;
  });
});

const showAssignOutletModal = createReducer(false, (builder) => {
  builder
    .addCase(actions.showBundleAssignOutletModal, () => true)
    .addCase(actions.hideBundleAssignOutletModal, () => false);
});

const assignedOutlets = createReducer([] as OutletsForm[], (builder) => {
  builder
    .addCase(actions.setAssignedBundleOutlets, (state, { payload }) => {
      return payload;
    })
    .addCase(actions.unsetBundleDetail, () => []);
});

const showManageSalesTypeModal = createReducer(false, (builder) => {
  builder
    .addCase(actions.showBundleManageSalesTypeModal, () => true)
    .addCase(actions.hideBundleManageSalesTypeModal, () => false);
});

const showChangeOutletSalesTypeModal = createReducer(false, (builder) => {
  builder
    .addCase(actions.showChangeOutletSalesTypeModal, () => true)
    .addCase(actions.hideChangeOutletSalesTypeModal, () => false);
});

const salesTypes = createReducer([] as SalesTypesForm[], (builder) => {
  builder.addCase(actions.setSalesTypes, (state, { payload }) => {
    return payload;
  });
});

const assignedSalesTypes = createReducer([] as SalesTypesForm[], (builder) => {
  builder.addCase(actions.setAssignedBundleSalesTypes, (state, { payload }) => {
    return payload;
  });
});

const isSalesTypeEnabled = createReducer(false, (builder) => {
  builder
    .addCase(actions.setIsEnabledBundleSalesTypes, (state, { payload }) => {
      const { isEnabled } = payload;
      return isEnabled;
    })
    .addCase(actions.disableSalesTypes, () => false)
    .addCase(actions.unsetBundleDetail, () => false);
});

type BundleComponentItemNameEntity = ItemNameEntity & {
  page?: number;
};
const bundleComponentItemNameInitialState: BundleComponentItemNameEntity = {
  results: [],
  completed: true,
  page: 1,
};
const bundleComponentItemName = createReducer(
  bundleComponentItemNameInitialState,
  (builder) => {
    builder
      .addCase(actions.setComponentsItemName, (state, { payload }) => {
        const { results, completed } = payload.itemName;
        const { page } = payload;
        state.results = results;
        state.completed = completed;
        state.page = page;
      })
      .addCase(
        actions.unsetBundleDetail,
        (): BundleComponentItemNameEntity => ({
          results: [],
          completed: true,
          page: 1,
        })
      );
  }
);

interface BundleComponentItemVariantNameEntity {
  itemVariants?: { [key: string]: ItemVariant };
  isLoading: boolean;
}
const bundleComponentItemVariantNameInitialState: BundleComponentItemVariantNameEntity =
  { isLoading: false, itemVariants: {} };
const bundleComponentItemVariantName = createReducer(
  bundleComponentItemVariantNameInitialState,
  (bundle) => {
    bundle
      .addCase(actions.setComponentsItemVariantName, (state, { payload }) => {
        return {
          isLoading: false,
          itemVariants: { ...payload },
        };
      })
      .addCase(actions.onClickItemName, (state) => {
        state.isLoading = true;
      })
      .addCase(
        actions.unsetBundleDetail,
        (): BundleComponentItemVariantNameEntity => ({
          isLoading: false,
          itemVariants: {},
        })
      );
  }
);

const showDeleteBundleModal = createReducer(false, (builder) => {
  builder
    .addCase(actions.showDeleteBundleModal, () => true)
    .addCase(actions.hideDeleteBundleModal, () => false);
});

const deletedBundle = createReducer(null as unknown as Bundles, (builder) => {
  builder.addCase(actions.setDeletedBundle, (state, { payload }) => {
    return payload;
  });
});

interface ErrorEntity {
  isError?: boolean;
  message?: string;
}
const bundleOutletError = createReducer(
  { isError: false, message: '' } as ErrorEntity,
  (builder) => {
    builder
      .addCase(actions.setBundleOutletError, (state, { payload }) => ({
        isError: true,
        message: payload,
      }))
      .addCase(actions.setAssignedBundleOutlets, () => ({
        isError: false,
        message: '',
      }))
      .addCase(actions.unsetBundleDetail, () => ({
        isError: false,
        message: '',
      }));
  }
);

const bundleComponentsError = createReducer(
  { isError: false, message: '' } as ErrorEntity,
  (builder) => {
    builder
      .addCase(actions.setBundleComponentsError, (state, { payload }) => ({
        isError: true,
        message: payload,
      }))
      .addCase(actions.resetBundleComponentsError, () => ({
        isError: false,
        message: '',
      }))
      .addCase(actions.unsetBundleDetail, () => ({
        isError: false,
        message: '',
      }));
  }
);

const bundleSalesTypesError = createReducer(
  { isError: false, message: '' } as ErrorEntity,
  (builder) => {
    builder.addCase(actions.setBundleSalesTypesError, (state, { payload }) => ({
      isError: true,
      message: payload,
    }));
    builder.addCase(actions.unsetBundleDetail, () => ({
      isError: false,
      message: '',
    }));
  }
);

interface BundleStatusConfirmationEntity {
  isShowed?: boolean;
  bundle?: Bundles;
  isActivate?: boolean;
  initialStatus?: boolean;
}
const bundleStatusConfirmationInitialState: BundleStatusConfirmationEntity = {
  isShowed: false,
  bundle: null as unknown as undefined,
  isActivate: false,
};
const bundleStatusConfirmation = createReducer(
  bundleStatusConfirmationInitialState,
  (bundle) => {
    bundle
      .addCase(
        actions.setUpdateBundleStatusConfirmation,
        (state, { payload }): BundleStatusConfirmationEntity => ({
          ...payload,
          isShowed: true,
        })
      )
      .addCase(
        actions.hideUpdateBundleStatusConfirmation,
        (state, { payload }): BundleStatusConfirmationEntity => ({
          initialStatus: payload,
          isShowed: false,
        })
      );
  }
);

const showAddItemModal = createReducer(
  { isShowing: false, index: -1 },
  (builder) => {
    builder
      .addCase(actions.addNewItem, (state, { payload }) => ({
        isShowing: true,
        index: payload.index,
      }))
      .addCase(actions.closeBundleItemModal, () => ({
        isShowing: false,
        index: -1,
      }));
  }
);

export type {
  BundlesEntity,
  BundleComponentItemVariantNameEntity,
  BundleStatusConfirmationEntity,
  ErrorEntity,
};

export default combineReducers({
  outlets,
  bundles,
  bundlePackageDetail,
  isShowingDetail,
  selectedOutlets,
  showAssignOutletModal,
  assignedOutlets,
  salesTypes,
  assignedSalesTypes,
  showManageSalesTypeModal,
  isSalesTypeEnabled,
  bundleComponentItemName,
  bundleComponentItemVariantName,
  showDeleteBundleModal,
  deletedBundle,
  bundleOutletError,
  bundleComponentsError,
  bundleSalesTypesError,
  bundleStatusConfirmation,
  showChangeOutletSalesTypeModal,
  showAddItemModal,
});
